import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Compass,
  Star,
  Sparkles,
  Clock,
  ArrowRight,
  Rocket,
  Castle,
  Globe,
  Brain,
  Wand2,
  Map
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO } from "@/hooks/useSEO";

const ageFeatures = [
  {
    icon: Clock,
    title: "Longer Stories",
    description: "8-12 minute stories with fuller narratives and chapters"
  },
  {
    icon: Brain,
    title: "Rich Vocabulary",
    description: "Age-appropriate words that expand reading skills"
  },
  {
    icon: Compass,
    title: "Adventure Plots",
    description: "Engaging storylines with challenges and resolutions"
  },
  {
    icon: BookOpen,
    title: "Character Depth",
    description: "Multi-dimensional characters with real emotions"
  }
];

const storyThemes = [
  { title: "Space Adventures", description: "Explore distant planets and meet alien friends", icon: Rocket, color: "from-indigo-400 to-purple-400" },
  { title: "Fantasy Quests", description: "Dragons, wizards, and magical kingdoms", icon: Castle, color: "from-purple-400 to-pink-400" },
  { title: "World Explorers", description: "Travel to real places and learn about cultures", icon: Globe, color: "from-blue-400 to-cyan-400" },
  { title: "Mystery Solving", description: "Fun puzzles and clues to figure out", icon: Map, color: "from-amber-400 to-orange-400" },
  { title: "Animal Heroes", description: "Brave animals on important missions", icon: Star, color: "from-green-400 to-emerald-400" },
  { title: "Magic & Wonder", description: "Enchanted worlds and magical powers", icon: Wand2, color: "from-pink-400 to-rose-400" }
];

const sampleStories = [
  {
    title: "The Dragon's Secret",
    description: "A young hero discovers a friendly dragon with an important mission",
    duration: "10 min"
  },
  {
    title: "Starship Discovery",
    description: "Exploring a mysterious planet with unexpected discoveries",
    duration: "12 min"
  },
  {
    title: "The Enchanted Library",
    description: "Books come alive in a magical library adventure",
    duration: "8 min"
  },
  {
    title: "Ocean Quest",
    description: "An underwater journey to help sea creatures",
    duration: "10 min"
  }
];

export default function Stories6to8Page() {
  useSEO({
    title: 'Stories for Ages 6-8 | Elementary Kids Stories | SnuggleCrafters',
    description: 'Engaging bedtime stories for children ages 6-8. Longer narratives with adventure, learning, and imagination perfect for growing readers.',
    keywords: 'stories for 6 year olds, stories for 7 year olds, stories for 8 year olds, elementary stories, kids adventure stories, chapter stories for kids',
    url: '/stories/age-6-8',
    type: 'website',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Stories', url: '/stories' },
      { name: 'Ages 6-8', url: '/stories/age-6-8' }
    ]
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 mb-6">
            <Compass className="h-4 w-4" />
            <span className="text-sm font-medium">Adventures for growing readers</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Bedtime Stories for Ages 6-8
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Captivating stories with real adventures, fascinating characters, and just the right amount of
            excitement before settling down to sleep.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link href="/auth">
              <Button size="lg" className="gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                <Sparkles className="h-5 w-5" />
                Create an Adventure Story
              </Button>
            </Link>
            <Link href="/features">
              <Button size="lg" variant="outline" className="gap-2">
                <BookOpen className="h-5 w-5" />
                Learn More
              </Button>
            </Link>
          </div>

          {/* Age indicator */}
          <div className="inline-flex items-center gap-4 px-6 py-3 rounded-2xl bg-white dark:bg-gray-800 shadow-lg">
            <span className="text-4xl">📚</span>
            <div className="text-left">
              <div className="font-bold text-lg">Ages 6, 7 & 8</div>
              <div className="text-sm text-gray-500">Growing Readers</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features for this age */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Built for Growing Minds</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Stories that challenge, engage, and still help wind down for sleep.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ageFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg text-center">
                <CardContent className="pt-6">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Story Themes */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Exciting Story Themes</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Adventures that spark imagination while winding down for sleep.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {storyThemes.map((theme, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer">
                <CardContent className="pt-6">
                  <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${theme.color} flex items-center justify-center mb-4`}>
                    <theme.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{theme.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{theme.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Stories */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Stories That Grow With Them</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Our AI creates stories tuned for the 6-8 age group with:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Richer vocabulary that builds reading skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Multi-scene adventures with satisfying conclusions</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Characters who learn, grow, and overcome challenges</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Exciting plots that still wind down peacefully</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <span>Educational elements woven naturally into stories</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-3xl p-8">
              <div className="text-center mb-6">
                <Rocket className="h-12 w-12 mx-auto text-blue-500 mb-2" />
                <h3 className="text-xl font-bold">Sample Stories</h3>
              </div>
              <div className="space-y-3">
                {sampleStories.map((story, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-semibold">{story.title}</h4>
                      <Badge variant="secondary" className="text-xs flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {story.duration}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{story.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Age Groups */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Stories for All Ages</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Have younger children too? We've got stories for them as well.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Link href="/stories/age-3-5">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-pink-100 text-pink-700 hover:bg-pink-100">Ages 3-5</Badge>
                  <CardTitle className="text-xl">Toddler Stories</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Simple, gentle stories perfect for little ones.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link href="/stories/educational">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-green-100 text-green-700 hover:bg-green-100">All Ages</Badge>
                  <CardTitle className="text-xl">Educational Stories</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Stories that teach values and knowledge while entertaining.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Start an Adventure Tonight</h2>
          <p className="text-xl opacity-90 mb-8">
            Create a personalized adventure story your child will remember.
          </p>
          <Link href="/auth">
            <Button size="lg" className="gap-2 bg-white text-blue-600 hover:bg-gray-100">
              <Sparkles className="h-5 w-5" />
              Create Your First Story Free
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
